import React, {useEffect, useState} from 'react'
import {useRouteMatch} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import styled from 'styled-components'
import HeaderV1 from 'pages/HealthCenter/Report/Elements/HeaderV1'
import useStyles from 'pages/Analysis/EEGSummary/Report/style'
import HealthCenterMajorPageV1 from 'pages/HealthCenter/ReportV1/HealthCenterMajorIndicators'
import useLanguage from 'features/languageReport/useLanguage'
import useEegSummaryReport from 'features/analysis/useEegSummaryReport'
import {
  AnalysisFollowupResult,
  getAnalysisFollowupResult,
  ReportFiveIndicatorScores,
} from 'api/analysisApi'
import {FiveFeaturesScore} from 'api/healthCareApi'
import {fetchPatientApi} from 'api/patientApi'
import {GenderValue, toGenderString} from 'constants/CommonConstant'
import {Spacing} from 'components/common/useBaseStyle'
import {INIT_TOP_INDICATORS_ANALYSIS_REPORT} from 'pages/Analysis/EEGSummary/constants'
import {ResHealthReport} from 'api/languageApi'
import {SCORE_HEALTH_REDUCE} from 'constants/HealthCenterConstant'

export const PageWrap = styled.div`
  background: white;
`
export const PageContent = styled.div`
  width: 1160px;
`

export const PageCard = styled.div`
  width: 100%;
  margin-bottom: 60px;
`
export const HeaderCard = styled.div`
  width: 100%;
  height: 40px;
  display: flex;
  border-bottom: 1px solid #00a29c;
`
export const HeaderCardNumber = styled.div`
  background-color: #2e4d6f;
  color: white;
  background-color: #2e4d6f;
  width: 42px;
  height: 42px;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0px 10px;
`

export const HeaderCardTitle = styled.div`
  font-size: 20px;
  height: 42px;
  display: flex;
  align-items: center;
  padding-left: 15px;
`

export const HeaderCardTitleGreen = styled.div`
  font-size: 18px;
  font: 'Noto Sans KR';
  font-weight: 500;
  line-height: 18px;
  height: 40px;
  display: flex;
  align-items: center;
  border-radius: 10px 10px 0px 0px;
  background-color: #00a29c;
  color: white;
  padding: 10px 24px 12px;
`

export const CardContent = styled.div`
  font-size: 14px;
`

export const Line = styled.div`
  height: 1px;
  background-color: #2e4d6f;
  width: 1150px;
`

export const EmptyBox = styled.div`
  width: 10px;
`

export const ReportCustomPadding = styled.div`
  padding: ${Spacing.report.reportCustomPadding}px;
`

export const FlexBox = styled.div`
  display: flex;
  padding: 0 15px;
  justify-content: space-between;
`

export interface HealthCenterMajorProps {
  fiveFeaturesRadarImage: string
  fiveFeaturesScore: FiveFeaturesScore
}

interface FiveIndicatorsReportProps {
  isLocalLanguage: boolean
  textHealthAPI: ResHealthReport | null
}

const FiveIndicatorsReport = ({
  isLocalLanguage,
  textHealthAPI,
}: FiveIndicatorsReportProps) => {
  const {t, i18n} = useTranslation()
  const {textHeaderAPI} = useLanguage('eeg')

  const {
    params: {id},
  } = useRouteMatch<{id: string}>()
  const classes = useStyles()

  const [patient, setPatient] = useState<PatientDetail | null>(null)
  const [fiveFeaturesScore, setFiveFeaturesScore] =
    useState<ReportFiveIndicatorScores>({
      sadness: 0,
      tension: 0,
      stress: 0,
      dominant_frequency: {
        score: 0,
        value: 0,
      },
      inattention: {
        score: 0,
        value: 0,
      },
    })

  const [urlImages, setUrlImages] = useState<string>('')
  const [dataAnalysisFollowupResult, setDataAnalysisFollowupResult] =
    useState<AnalysisFollowupResult>(INIT_TOP_INDICATORS_ANALYSIS_REPORT)

  const {items, getReport} = useEegSummaryReport()

  const getAnalysisFollowupResultApi = async () => {
    const response = await getAnalysisFollowupResult(Number(id), i18n.language)
    setDataAnalysisFollowupResult(response)
  }

  function normalizeScore(score: number): number {
    if (score < SCORE_HEALTH_REDUCE.low) return SCORE_HEALTH_REDUCE.low
    if (score > SCORE_HEALTH_REDUCE.high) return SCORE_HEALTH_REDUCE.high
    return score
  }

  function normalizeFiveFeatures(data: FiveFeaturesScore): FiveFeaturesScore {
    return {
      sadness: normalizeScore(data?.sadness ?? 0),
      tension: normalizeScore(data?.tension ?? 0),
      stress: normalizeScore(data?.stress ?? 0),
      dominant_frequency: {
        score: normalizeScore(data?.dominant_frequency?.score ?? 0),
        value: data?.dominant_frequency?.value ?? 0,
      },
      inattention: {
        score: normalizeScore(data?.inattention?.score ?? 0),
        value: data?.inattention?.value ?? 0,
      },
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      if (items?.result) {
        const user = await fetchPatientApi({
          uuid: items.patientUuid,
        })
        if (user?.data) {
          setPatient(user.data)
        }
      }
    }
    fetchData()
  }, [items])
  useEffect(() => {
    const fetchData = async () => {
      if (dataAnalysisFollowupResult) {
        setUrlImages(dataAnalysisFollowupResult.images.radar_chart ?? '')
        setFiveFeaturesScore(
          dataAnalysisFollowupResult.scores as ReportFiveIndicatorScores,
        )
      }
    }
    fetchData()
  }, [dataAnalysisFollowupResult])

  useEffect(() => {
    const param = {
      id: Number.parseInt(id, 10),
      payload: {
        lang: i18n.language,
      },
    }
    getReport(param)
    getAnalysisFollowupResultApi()
  }, [i18n.language])

  return (
    <>
      <HeaderV1
        title={textHealthAPI?.five_major_features.title ?? ''}
        content={textHealthAPI?.header.subject_info_guide ?? ''}
        name={t('IGetFullName', {
          firstName: items?.patientFirstName ?? '',
          lastName: items?.patientLastName ?? '',
        })}
        sex={
          patient?.gender
            ? t(toGenderString(patient?.gender as GenderValue))
            : ''
        }
        birthday={patient?.birth ?? ''}
        measurementDate={String(items?.measureDate)}
        textHeader={textHeaderAPI}
        isLocalLanguage={isLocalLanguage}
        eceo=''
        isShowEceo={false}
      />
      {items?.result ? (
        <HealthCenterMajorPageV1
          fiveFeaturesRadarImage={urlImages}
          fiveFeaturesScore={normalizeFiveFeatures(fiveFeaturesScore)}
          isEEG
          scoreAnnotation={
            textHealthAPI?.common?.score_annotation?.t_score ?? ''
          }
          footer={textHealthAPI?.common?.footer?.[0] ?? ''}
        />
      ) : (
        <div className={classes.hrvError}>
          {t('IHealthReportTabNull', {tab: t('IBrainFunctionAdvance')})}
        </div>
      )}
    </>
  )
}
export default FiveIndicatorsReport
